'use client';
import moment from 'moment';
import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import EmailIcon from '../../icons/Email';
import EmailReadIcon from '../../icons/EmailRead';
import StatusDotSolidIcon from '../../icons/StatusDotSolid';
import TrashIcon from '../../icons/Trash';
import LineClamp from '../../utils/LineClamp';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';
import Divider from '../Divider';
import IconButton from '../IconButton';
import ListItemIcon from '../ListItemIcon';
import ListItemText from '../ListItemText';
import MenuItem from '../MenuItem';
import Tooltip from '../Tooltip';
import Typography from '../Typography';
import getTimestampAsText from './getTimestampAsText';

export const styles = ({ idsTokens = idsTokensDefault }, { variant = 'classic' }, classes) => ({
  menuItem: {
    height: 'unset',
    whiteSpace: 'normal',
    /**
     * @TODO this should become unnecessary when the List component is updated
     * to not have a narrower borderBottom when pressed
     */
    borderBottomWidth: '2px !important',
    '&.MuiMenuItem-root': {
      paddingTop: idsTokens.navbar[variant].spacing.menuItem.top,
      paddingBottom: idsTokens.navbar[variant].spacing.menuItem.bottom,
      paddingLeft: idsTokens.navbar[variant].spacing.menuItem.left,
      paddingRight: idsTokens.navbar[variant].spacing.menuItem.right,
    },
    '& .MuiTypography-root': {
      color: idsTokens.listItem.default.color.text.label.enabled,
    },
    '&:hover .MuiTypography-root': {
      color: idsTokens.listItem.default.color.text.label.hover,
    },
    '&:focus-visible:not(:hover) .MuiTypography-root': {
      color: idsTokens.listItem.default.color.text.label.focus,
    },
    '&:active .MuiTypography-root': {
      color: idsTokens.listItem.default.color.text.label.pressed,
    },
  },
  iconContainer: {
    minWidth: 0,
    height: 20,
    marginRight: 8,
    marginTop: 7,
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
  },
  unreadMarker: {
    color: idsTokens.badge.default.color.background.dot,
    width: 18,
    height: idsTokens.badge.default.spacing.standard.min.dotHeight,
    visibility: 'hidden',
    '[data-unread] &': {
      visibility: 'visible',
    },
  },
  notificationIcon: {
    marginLeft: 7,
    marginRight: 6,
    [`.${classes.menuItem} &`]: {
      color: idsTokens.listItem.default.color.icon.enabled,
    },
    [`.${classes.menuItem}:hover &`]: {
      color: idsTokens.listItem.default.color.icon.hover,
    },
    [`.${classes.menuItem}:focus-visible:not(:hover) &`]: {
      color: idsTokens.listItem.default.color.icon.focus,
    },
    [`.${classes.menuItem}:active &`]: {
      color: idsTokens.listItem.default.color.icon.pressed,
    },
  },
  trailingElements: {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'start',
  },
  timestamp: {
    color: idsTokens.listItem.default.color.text.sub.enabled,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    color: idsTokens.iconButton.default.color.icon.enabled,
  },
  divider: {
    '&.MuiDivider-root': {
      marginTop: idsTokens.navbar[variant].spacing.divider.top,
      marginRight: idsTokens.navbar[variant].spacing.divider.right,
      marginLeft: 44,
      marginBottom: idsTokens.navbar[variant].spacing.divider.bottom,
      padding: '0px',
    },
  },
});

const useStyles = makeStyles()(styles);

export const NotificationsItem = ({
  id,
  timestamp,
  handleClose,
  onClick,
  icon: Icon,
  header,
  details,
  onToggleRead,
  onDelete,
  unread,
  removeText,
  markReadText,
  markUnreadText,
  hasDivider,
  variant,
  forwardedRef: ref,
  ...rest
}) => {
  const { classes } = useStyles({ variant });

  const currentDate = moment().format('YYYYMMDD');

  const memoizedTimestampText = useMemo(
    () => getTimestampAsText(timestamp, currentDate, 'YYYYMMDD'),
    [timestamp, currentDate]
  );

  const detailsContent =
    typeof details === 'string' ? <LineClamp lines={2}>{details}</LineClamp> : details;

  const [readTooltipOpen, setReadTooltipOpen] = React.useState(false);

  const handleReadTooltipClose = () => {
    setReadTooltipOpen(false);
  };

  const handleReadTooltipOpen = () => {
    setReadTooltipOpen(true);
  };

  return (
    <>
      {hasDivider && <Divider type="light" className={classes.divider} />}
      <MenuItem
        onClick={() => {
          onClick && onClick(id);
          handleClose && handleClose();
        }}
        className={classes.menuItem}
        data-unread={unread ? true : undefined}
        {...rest}
        ref={ref}
        disableRipple
      >
        <ListItemIcon className={classes.iconContainer}>
          <StatusDotSolidIcon fontSize="extraSmall" className={classes.unreadMarker} />
          {Icon && <Icon fontSize="small" className={classes.notificationIcon} />}
        </ListItemIcon>

        <ListItemText
          disableTypography
          primary={
            <Typography variant="bodyDefault" emphasis="low">
              {header}
            </Typography>
          }
          secondary={
            <Typography variant="bodySmall" emphasis="low" component="div">
              {detailsContent}
            </Typography>
          }
        />
        <div className={classes.trailingElements}>
          <div className={classes.actionButtons}>
            {onToggleRead && (
              <Tooltip
                title={unread ? markReadText : markUnreadText}
                placement="top"
                open={readTooltipOpen}
                onClose={handleReadTooltipClose}
                onOpen={handleReadTooltipOpen}
              >
                <IconButton
                  size="small"
                  aria-label={unread ? markReadText : markUnreadText}
                  onClick={(e) => {
                    setReadTooltipOpen(false);
                    // avoid calling the onClick handler in the parent
                    e.stopPropagation();
                    onToggleRead(id);
                  }}
                >
                  {unread ? <EmailReadIcon /> : <EmailIcon />}
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title={removeText} placement="top">
                <IconButton
                  destructiveAction
                  size="small"
                  aria-label={removeText}
                  onClick={(e) => {
                    // avoid calling the onClick handler in the parent
                    e.stopPropagation();
                    onDelete(id);
                  }}
                >
                  <TrashIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <Typography className={classes.timestamp} variant="caption" component="p">
            {memoizedTimestampText}
          </Typography>
        </div>
      </MenuItem>
    </>
  );
};

export default withRef()(NotificationsItem);
